/**
 * ManageSubscriptionModal
 *
 * Modal for managing an organization's subscription.
 * Allows superadmins to:
 * - View current subscription status
 * - Apply plan changes (initial assignment, upgrades)
 * - Request downgrades (requires approval)
 * - Choose application mode (immediate/scheduled)
 * - View pending change requests
 *
 * Anti-fraud rules:
 * - Downgrades can only be scheduled for next billing period
 * - Only downgrades require approval from another superadmin
 * - Initial assignments and upgrades are applied immediately
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CalendarDaysIcon,
  BoltIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { apiService } from '@/services/api';
import { SubscriptionPlan } from '@/stores/subscriptionStore';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { formatPrice, formatLimit, getEffectivePrice } from '@/utils/subscriptionFormat';
import { tierRank } from '../../utils/planTiers';

interface Organization {
  id: string;
  name: string;
  subdomain?: string;
}

interface Subscription {
  id: string;
  planId?: string;
  plan?: {
    name: string;
    tier: string;
  };
  status: string;
  usage?: {
    currentEncountersThisMonth: number;
    currentTranscriptionMinutes: number;
  };
  included?: {
    encountersPerMonth: number;
    transcriptionMinutes: number;
  };
  period?: {
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
  };
}

interface ChangeRequest {
  id: string;
  status: string;
  changeType: string;
  requestedPlanId: string;
  requestedPlanName: string;
  applicationMode: 'immediate' | 'scheduled';
  scheduledFor?: Date;
  requestedAt: Date;
  requestedBy: { firstName: string; lastName: string; email: string };
  billingImpact: {
    newMonthlyAmount: number;
    proratedCredit?: number;
    proratedCharge?: number;
    effectiveDate: Date;
  };
}

interface SubscriptionStatus {
  currentSubscription: Subscription | null;
  currentPlan: SubscriptionPlan | null;
  availablePlans: SubscriptionPlan[];
  pendingRequest: ChangeRequest | null;
}

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization;
  onSuccess?: () => void;
}

type ApplicationMode = 'immediate' | 'scheduled';

export function ManageSubscriptionModal({
  isOpen,
  onClose,
  organization,
  onSuccess
}: ManageSubscriptionModalProps) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);

  // Form state
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [applicationMode, setApplicationMode] = useState<ApplicationMode>('immediate');
  const [reason, setReason] = useState('');

  // Load subscription status
  const loadSubscriptionStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get<{ success: boolean; data: SubscriptionStatus }>(
        `/v1/platform/subscriptions/${organization.id}/status`
      );
      setStatus(response.data);

      // Pre-select current plan if exists
      if (response.data.currentPlan) {
        setSelectedPlanId(response.data.currentPlan.id);
      }
    } catch (error) {
      console.error('Failed to load subscription status:', error);
      toast.error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  }, [organization.id]);

  useEffect(() => {
    if (isOpen && organization.id) {
      loadSubscriptionStatus();
    }
  }, [isOpen, organization.id, loadSubscriptionStatus]);

  // Determine change type
  const getChangeType = (): 'upgrade' | 'downgrade' | 'initial_assignment' | null => {
    if (!selectedPlanId || !status) return null;

    const currentPlan = status.currentPlan;
    const newPlan = status.availablePlans.find((p) => p.id === selectedPlanId);

    if (!newPlan) return null;
    if (!currentPlan) return 'initial_assignment';
    if (currentPlan.id === selectedPlanId) return null;

    const currentTier = tierRank(currentPlan.tier);
    const newTier = tierRank(newPlan.tier);

    if (newTier > currentTier) return 'upgrade';
    if (newTier < currentTier) return 'downgrade';

    // Same tier - compare by price
    if (getEffectivePrice(newPlan) > getEffectivePrice(currentPlan)) return 'upgrade';
    if (getEffectivePrice(newPlan) < getEffectivePrice(currentPlan)) return 'downgrade';

    return null;
  };

  const changeType = getChangeType();
  const isDowngrade = changeType === 'downgrade';
  const selectedPlan = status?.availablePlans.find((p) => p.id === selectedPlanId);
  const isNewPlanSelected = selectedPlanId && selectedPlanId !== status?.currentPlan?.id;

  // Force scheduled mode for downgrades
  useEffect(() => {
    if (isDowngrade) {
      setApplicationMode('scheduled');
    }
  }, [isDowngrade]);

  const handleSubmit = async () => {
    if (!selectedPlanId || !isNewPlanSelected) return;

    setSubmitting(true);
    try {
      // Only downgrades require approval - initial assignments and upgrades are applied directly
      if (changeType === 'downgrade') {
        await apiService.post(`/v1/platform/subscriptions/${organization.id}/request-change`, {
          planId: selectedPlanId,
          applicationMode,
          reason: reason.trim() || undefined
        });
        toast.success('Subscription downgrade request created. Awaiting approval from another superadmin.');
      } else {
        // Initial assignment or upgrade - apply directly without approval
        await apiService.post(`/v1/platform/subscriptions/direct-assign`, {
          organizationIds: [organization.id],  // API expects an array
          planId: selectedPlanId
        });
        toast.success('Subscription updated successfully.');
      }
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to update subscription:', error);
      toast.error(error.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSubmitting(false);
    }
  };

  // Suspend / cancel / reactivate the current subscription (SaaS lifecycle controls).
  const currentStatus = status?.currentSubscription?.status;
  const canSuspendOrCancel = currentStatus === 'active' || currentStatus === 'trialing';
  const canReactivate = currentStatus === 'suspended' || currentStatus === 'cancelled' || currentStatus === 'expired';

  const handleLifecycle = async (action: 'suspend' | 'cancel' | 'reactivate') => {
    const labels: Record<typeof action, string> = { suspend: 'suspend', cancel: 'cancel', reactivate: 'reactivate' };
    if (action !== 'reactivate' && !window.confirm(`Are you sure you want to ${labels[action]} this subscription? Billable usage will be blocked.`)) {
      return;
    }
    setSubmitting(true);
    try {
      await apiService.post(`/v1/platform/subscriptions/${organization.id}/${action}`, {
        reason: reason.trim() || undefined
      });
      toast.success(`Subscription ${action === 'reactivate' ? 'reactivated' : action + 'ed'}.`);
      await loadSubscriptionStatus();
      onSuccess?.();
    } catch (error: any) {
      console.error(`Failed to ${action} subscription:`, error);
      toast.error(error.response?.data?.error || `Failed to ${action} subscription`);
    } finally {
      setSubmitting(false);
    }
  };

  // formatPrice, formatLimit, getEffectivePrice imported from shared utils

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manage Subscription</h2>
              <p className="text-sm text-gray-600">{organization.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Pending Request Alert */}
                {status?.pendingRequest && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                    <div className="flex items-start gap-3">
                      <ClockIcon className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-amber-800">Pending Change Request</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          A request to change to <strong>{status.pendingRequest.requestedPlanName}</strong> is
                          awaiting approval.
                          {status.pendingRequest.applicationMode === 'scheduled' && status.pendingRequest.scheduledFor && (
                            <> Scheduled for {format(new Date(status.pendingRequest.scheduledFor), 'MMM d, yyyy')}.</>
                          )}
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          Requested by {status.pendingRequest.requestedBy.firstName}{' '}
                          {status.pendingRequest.requestedBy.lastName} on{' '}
                          {format(new Date(status.pendingRequest.requestedAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Plan */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Current Plan</h3>
                  {status?.currentSubscription ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-lg font-semibold text-gray-900">
                            {status.currentSubscription.plan?.name || 'Custom Plan'}
                          </span>
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            {status.currentSubscription.status}
                          </span>
                        </div>
                        {status.currentPlan && (
                          <span className="text-lg font-bold text-gray-900">
                            {formatPrice(getEffectivePrice(status.currentPlan), status.currentPlan.pricing.billingCycle)}
                          </span>
                        )}
                      </div>
                      {status.currentSubscription.usage && status.currentSubscription.included && (
                        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Encounters:</span>
                            <span className="ml-2 font-medium">
                              {status.currentSubscription.usage.currentEncountersThisMonth} /{' '}
                              {formatLimit(status.currentSubscription.included.encountersPerMonth)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Minutes:</span>
                            <span className="ml-2 font-medium">
                              {Math.round(status.currentSubscription.usage.currentTranscriptionMinutes)} /{' '}
                              {formatLimit(status.currentSubscription.included.transcriptionMinutes)}
                            </span>
                          </div>
                        </div>
                      )}
                      {status.currentSubscription.period && (
                        <p className="text-xs text-gray-500 mt-2">
                          Current period: {format(new Date(status.currentSubscription.period.currentPeriodStart), 'MMM d')} -{' '}
                          {format(new Date(status.currentSubscription.period.currentPeriodEnd), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-gray-500">
                      No subscription assigned
                    </div>
                  )}
                </div>

                {/* Plan Selection */}
                {!status?.pendingRequest && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-3">Select New Plan</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {status?.availablePlans.map((plan) => {
                          const isSelected = selectedPlanId === plan.id;
                          const isCurrent = status.currentPlan?.id === plan.id;

                          return (
                            <button
                              key={plan.id}
                              onClick={() => setSelectedPlanId(plan.id)}
                              className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                                isSelected
                                  ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                            >
                              {isCurrent && (
                                <span className="absolute top-2 right-2 text-xs font-medium text-gray-500">
                                  Current
                                </span>
                              )}
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className={`w-3 h-3 rounded-full ${
                                    plan.tier === 'basic'
                                      ? 'bg-gray-400'
                                      : plan.tier === 'professional'
                                        ? 'bg-blue-500'
                                        : plan.tier === 'enterprise'
                                          ? 'bg-purple-500'
                                          : 'bg-amber-500'
                                  }`}
                                />
                                <span className="font-semibold text-gray-900">{plan.name}</span>
                              </div>
                              <p className="text-lg font-bold text-gray-900">
                                {formatPrice(getEffectivePrice(plan), plan.pricing.billingCycle)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {formatLimit(plan.included.encountersPerMonth)} encounters/mo
                              </p>
                              {isSelected && (
                                <div className="absolute bottom-2 right-2">
                                  <CheckIcon className="h-5 w-5 text-purple-600" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Application Mode */}
                    {isNewPlanSelected && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Application Mode</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => !isDowngrade && setApplicationMode('immediate')}
                            disabled={isDowngrade}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              applicationMode === 'immediate'
                                ? 'border-purple-500 bg-purple-50'
                                : isDowngrade
                                  ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <BoltIcon className="h-5 w-5 text-purple-600" />
                              <span className="font-medium text-gray-900">Apply Immediately</span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Changes take effect now with pro-rated billing
                            </p>
                          </button>

                          <button
                            onClick={() => setApplicationMode('scheduled')}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              applicationMode === 'scheduled'
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <CalendarDaysIcon className="h-5 w-5 text-indigo-600" />
                              <span className="font-medium text-gray-900">Schedule for Next Period</span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {status?.currentSubscription?.period?.currentPeriodEnd
                                ? `Takes effect ${format(new Date(status.currentSubscription.period.currentPeriodEnd), 'MMM d, yyyy')}`
                                : 'Takes effect at start of next billing period'}
                            </p>
                          </button>
                        </div>

                        {isDowngrade && (
                          <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
                            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                            <span>
                              Downgrades can only be scheduled for the next billing period to prevent fraud.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Change Summary */}
                    {isNewPlanSelected && selectedPlan && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                          <ChartBarIcon className="h-5 w-5 text-gray-600" />
                          Change Summary
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Change Type:</span>
                            <span
                              className={`font-medium flex items-center gap-1 ${
                                changeType === 'upgrade'
                                  ? 'text-green-600'
                                  : changeType === 'downgrade'
                                    ? 'text-amber-600'
                                    : 'text-blue-600'
                              }`}
                            >
                              {changeType === 'upgrade' && <ArrowUpIcon className="h-4 w-4" />}
                              {changeType === 'downgrade' && <ArrowDownIcon className="h-4 w-4" />}
                              {changeType === 'initial_assignment' ? 'Initial Assignment' : changeType}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">New Plan:</span>
                            <span className="font-medium text-gray-900">{selectedPlan.name}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">New Price:</span>
                            <span className="font-medium text-gray-900">
                              {formatPrice(getEffectivePrice(selectedPlan), selectedPlan.pricing.billingCycle)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Application:</span>
                            <span className="font-medium text-gray-900">
                              {applicationMode === 'immediate' ? 'Immediate' : 'Scheduled'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reason */}
                    {isNewPlanSelected && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reason (optional)
                        </label>
                        <textarea
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Provide a reason for this change..."
                          rows={2}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    )}

                    {/* Approval Notice - only for downgrades */}
                    {isNewPlanSelected && isDowngrade && (
                      <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 rounded-lg p-3">
                        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <span>
                          Downgrades require approval from another superadmin before taking effect.
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            {/* Lifecycle controls (left) */}
            <div className="flex items-center gap-2">
              {canSuspendOrCancel && (
                <>
                  <button
                    onClick={() => handleLifecycle('suspend')}
                    disabled={submitting}
                    className="px-3 py-2 text-sm font-medium text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 disabled:opacity-50"
                    title="Pause billable usage; reversible"
                  >
                    Suspend
                  </button>
                  <button
                    onClick={() => handleLifecycle('cancel')}
                    disabled={submitting}
                    className="px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    title="Cancel the subscription"
                  >
                    Cancel Subscription
                  </button>
                </>
              )}
              {canReactivate && (
                <button
                  onClick={() => handleLifecycle('reactivate')}
                  disabled={submitting}
                  className="px-3 py-2 text-sm font-medium text-green-700 bg-white border border-green-300 rounded-lg hover:bg-green-50 disabled:opacity-50"
                  title="Reactivate the subscription"
                >
                  Reactivate
                </button>
              )}
            </div>

            {/* Plan-change actions (right) */}
            <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
            {!status?.pendingRequest && isNewPlanSelected && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    {isDowngrade ? 'Submitting...' : 'Applying...'}
                  </>
                ) : (
                  isDowngrade ? 'Request Downgrade' : 'Apply Change'
                )}
              </button>
            )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ManageSubscriptionModal;
